/**
 * Channel classifier (клиентская версия) — для предклассификации в UI.
 *
 * ВАЖНО: основная классификация делается на сервере (services/attribution/channels.js).
 * Эта клиентская версия используется ТОЛЬКО для:
 *   - быстрого отображения «Спасибо, вы пришли с ВК» до server response
 *   - first-touch snapshot в localStorage
 *
 * Поддерживает все 28 click IDs и базовые GA4 channel rules.
 * При расхождении с сервером — сервер всегда win (он переписывает в БД).
 */

import type { Channel, ClickIds, UtmParams } from './types';

const SEARCH_ENGINES = new Set([
  'google.com', 'google.ru', 'bing.com', 'yahoo.com',
  'yandex.ru', 'yandex.com', 'duckduckgo.com', 'mail.ru', 'go.mail.ru',
  'rambler.ru',
]);

const SOCIAL_SITES = new Set([
  'vk.com', 'm.vk.com', 'ok.ru',
  'facebook.com', 'fb.com', 'm.facebook.com', 'instagram.com',
  'twitter.com', 'x.com', 't.co',
  't.me', 'telegram.me',
  'tiktok.com', 'linkedin.com', 'pinterest.com', 'reddit.com',
  'youtube.com', 'youtu.be', 'm.youtube.com',
  'dzen.ru', 'zen.yandex.ru',
]);

const CLICK_ID_HINTS: Record<string, { channel: Channel; source: string; medium: string }> = {
  gclid:       { channel: 'paid_search', source: 'google',    medium: 'cpc' },
  gbraid:      { channel: 'paid_search', source: 'google',    medium: 'cpc' },
  wbraid:      { channel: 'paid_search', source: 'google',    medium: 'cpc' },
  dclid:       { channel: 'display',     source: 'google',    medium: 'display' },
  yclid:       { channel: 'paid_search', source: 'yandex',    medium: 'cpc' },
  ymclid:      { channel: 'paid_search', source: 'yandex',    medium: 'cpc' },
  yadclid:     { channel: 'paid_search', source: 'yandex',    medium: 'cpc' },
  msclkid:     { channel: 'paid_search', source: 'bing',      medium: 'cpc' },
  fbclid:      { channel: 'paid_social', source: 'facebook',  medium: 'paid_social' },
  ttclid:      { channel: 'paid_social', source: 'tiktok',    medium: 'paid_social' },
  twclid:      { channel: 'paid_social', source: 'twitter',   medium: 'paid_social' },
  li_fat_id:   { channel: 'paid_social', source: 'linkedin',  medium: 'paid_social' },
  rdt_cid:     { channel: 'paid_social', source: 'reddit',    medium: 'paid_social' },
  sccid:       { channel: 'paid_social', source: 'snapchat',  medium: 'paid_social' },
  epik:        { channel: 'paid_social', source: 'pinterest', medium: 'paid_social' },
  rb_clickid:  { channel: 'paid_social', source: 'vk',        medium: 'paid_social' },
  vk_click_id: { channel: 'paid_social', source: 'vk',        medium: 'paid_social' },
  mt_click_id: { channel: 'paid_social', source: 'mytarget',  medium: 'paid_social' },
  mc_eid:      { channel: 'email',       source: 'mailchimp', medium: 'email' },
};

const OWN_DOMAINS = [
  'aziatochka.ru',
  'www.aziatochka.ru',
  'new.aziatochka.ru',
  'xn--80aaa0afn1a5a0c.xn--p1ai',
];

function extractDomain(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return null;
  }
}

function isOwnDomain(domain: string | null): boolean {
  if (!domain) return false;
  return OWN_DOMAINS.some((d) => domain === d || domain.endsWith('.' + d));
}

export interface ClientClassification {
  channel: Channel;
  source: string | null;
  medium: string | null;
  campaign: string | null;
  content: string | null;
  term: string | null;
  clickIdType: string | null;
  clickIdValue: string | null;
  referringDomain: string | null;
}

/**
 * Клиентская предклассификация. Серверная версия более точная и переписывает БД.
 */
export function classifyClientSide(input: {
  url: string;
  referrer: string;
  utm: UtmParams;
  clickIds: ClickIds;
}): ClientClassification {
  const utm = input.utm || {};
  const clickIds = input.clickIds || {};

  const referringDomain = extractDomain(input.referrer);
  const isSelfRef = isOwnDomain(referringDomain);
  const effectiveRef = isSelfRef ? null : referringDomain;

  // Click ID — приоритет
  let clickIdType: string | null = null;
  let clickIdValue: string | null = null;
  let hint: { channel: Channel; source: string; medium: string } | null = null;

  for (const [key, val] of Object.entries(clickIds)) {
    if (val) {
      clickIdType = key;
      clickIdValue = String(val);
      if (CLICK_ID_HINTS[key]) {
        hint = CLICK_ID_HINTS[key];
      }
      break;
    }
  }

  // Определяем channel
  let channel: Channel = 'unassigned';

  if (hint) {
    channel = hint.channel;
  } else if (!utm.source && !utm.medium && !effectiveRef) {
    channel = 'direct';
  } else if (utm.medium && /^(.*cp.*|ppc|paid.*)/i.test(utm.medium)) {
    if (utm.source && SEARCH_ENGINES.has(utm.source + '.ru') || utm.source === 'yandex' || utm.source === 'google' || utm.source === 'bing') {
      channel = 'paid_search';
    } else if (utm.source && (SOCIAL_SITES.has(utm.source + '.com') || utm.source === 'vk' || utm.source === 'fb' || utm.source === 'instagram')) {
      channel = 'paid_social';
    } else {
      channel = 'paid_other';
    }
  } else if (utm.medium === 'email' || utm.source === 'email') {
    channel = 'email';
  } else if (utm.medium === 'social' || utm.medium === 'paid_social') {
    channel = 'organic_social';
  } else if (utm.medium === 'organic') {
    channel = 'organic_search';
  } else if (effectiveRef && SEARCH_ENGINES.has(effectiveRef)) {
    channel = 'organic_search';
  } else if (effectiveRef && SOCIAL_SITES.has(effectiveRef)) {
    channel = 'organic_social';
  } else if (effectiveRef) {
    channel = 'referral';
  }

  return {
    channel,
    source: utm.source || hint?.source || effectiveRef || (channel === 'direct' ? '(direct)' : null),
    medium: utm.medium || hint?.medium || (channel === 'direct' ? '(none)' : null),
    campaign: utm.campaign || null,
    content: utm.content || null,
    term: utm.term || null,
    clickIdType,
    clickIdValue,
    referringDomain: effectiveRef,
  };
}
